import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '#client/lib/api';

interface Settled<T> {
  data?: T;
  error?: string;
  /** Which request produced this state; compared against the current key. */
  key: string;
}

export interface KeyedRequest<T> {
  data?: T | undefined;
  loading: boolean;
  error?: string | undefined;
  /** Re-runs the current request. */
  refresh: () => void;
}

/**
 * One fetch, keyed by a string that describes it.
 *
 * This is the engine under every data hook in the app — the list hook, the
 * item hook, the dashboard, the worklist and the command palette — so these
 * four decisions are made once and cannot drift between copies:
 *
 *   * `loading` is **derived** by comparing the current key against the key of
 *     the last settled result, never toggled with setState inside the effect.
 *     The obvious `setLoading(true)` version is what the React Compiler lint
 *     rejects, and it renders one extra time per request.
 *   * The previous request is aborted before a new one starts, so a fast typist
 *     cannot have an earlier response overwrite a later one. Creating a fresh
 *     controller per run without aborting the last one would not achieve this.
 *   * A settled result is ignored if its own controller aborted.
 *   * Failures become a message, never a thrown promise. The palette swallowed
 *     its errors entirely, so a failed search looked exactly like no results.
 *
 * Pass `key: undefined` to skip fetching — an "add" form reusing an "edit"
 * form's component, for instance.
 *
 * `run` receives the AbortSignal and must pass it to `api`. It is called only
 * when `key` changes, so it does not need to be referentially stable; the key
 * is the dependency, which is the whole point of the design.
 */
export function useKeyedRequest<T>(
  key: string | undefined,
  run: (signal: AbortSignal) => Promise<T>,
  errorMessage: string,
): KeyedRequest<T> {
  const [reloadToken, setReloadToken] = useState(0);
  const [settled, setSettled] = useState<Settled<T>>({ key: '' });
  const abortRef = useRef<AbortController>(null);

  const requestKey = key === undefined ? undefined : `${reloadToken}:${key}`;

  // `run` is deliberately not a dependency: it is a fresh closure on every
  // render, and re-running on identity alone would loop forever. The key is
  // what says the request changed.
  //
  // The ref is updated in an effect rather than during render — writing to a
  // ref while rendering is what the React Compiler lint rejects. This effect is
  // declared first, so on any commit it runs before the fetch below reads it.
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  });

  useEffect(() => {
    if (requestKey === undefined) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    runRef
      .current(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setSettled({ data, key: requestKey });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setSettled({
          error: cause instanceof ApiError ? cause.message : errorMessage,
          key: requestKey,
        });
      });

    return () => controller.abort();
  }, [requestKey, errorMessage]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  return useMemo(
    () => ({
      data: settled.data,
      error: settled.error,
      loading: requestKey !== undefined && settled.key !== requestKey,
      refresh,
    }),
    [settled, requestKey, refresh],
  );
}
